import { useEffect, useMemo, useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { useReviewCreationData } from 'reducers/reviewCreationSlice';
import { CustomHeaderCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CustomHeaderCell';

import { REVIEW_PROVIDER_TYPES } from 'constants/ProjectConstants';

import { AccordionPanel2 } from 'uiToolkitV1/Accordion/Accordion';
import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';
import { TooltipInfoV1 } from 'uiToolkitV1/TooltipInfoV1';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { useTemplateMetadata } from '../../../Hooks/dataHooks/shareResultsDataHooks';
import { ResponseTableDropdown } from './ReviewerTypeVisibilitySettingsSelfDemo';

const AnonymityDropdown = ({
  data,
}: {
  data: { anonymity: { checked: boolean; disabled: boolean }; reviewer: string };
}) => {
  const { anonymity, reviewer } = data;
  const anonymityOptions = [
    { value: 'Revealed', label: 'Revealed' },
    { value: 'Anonymous', label: 'Anonymous' },
  ];

  const isAnonymityNotApplicable = reviewer === 'Dotted line manager' || reviewer === 'Manager';
  if (isAnonymityNotApplicable) {
    return null;
  }
  return (
    <ResponseTableDropdown
      options={anonymityOptions}
      defaultValue={anonymity.checked ? anonymityOptions[1] : anonymityOptions[0]}
      disabled={anonymity.disabled}
    />
  );
};

const ReviewerTypeCellRenderer = ({ value }) => <TextBodyMedium>{value}</TextBodyMedium>;

export const ReviewerTypeVisibilitySettingsManagerDemo = ({ feedbackFormId }) => {
  const { reviewCycleId } = useReviewCreationData();

  const [rowsData, setRowsData] = useState([]);
  const [columnDefs, setColumnDefs] = useState([]);

  const managerTemplateMetadata = useTemplateMetadata({
    reviewCycleId,
    feedbackFormId,
    providerType: REVIEW_PROVIDER_TYPES.MANAGER,
  });

  const hasGoalQuestions =
    managerTemplateMetadata?.template?.templateSetting?.overallRatingDistribution?.PERFORMANCE > 0;
  const hasCompetencyQuestions =
    managerTemplateMetadata?.template?.templateSetting?.overallRatingDistribution?.POTENTIAL > 0;
  const hasQNAQuestions = managerTemplateMetadata?.template?.templateSetting?.overallRatingDistribution?.QNA > 0;

  const hasPeer = useTemplateMetadata({
    reviewCycleId,
    feedbackFormId,
    providerType: REVIEW_PROVIDER_TYPES.PEER,
  });
  const hasUpward = useTemplateMetadata({
    reviewCycleId,
    feedbackFormId,
    providerType: REVIEW_PROVIDER_TYPES.UPWARD,
  });

  const commonColumnsDef = useMemo(
    () => ({
      headerComponent: CustomHeaderCell,
      headerComponentParams: {
        enableMenu: false,
        enableSorting: false,
      },
      cellStyle: { display: 'flex', alignItems: 'center', height: '100%' },
    }),
    []
  );

  interface AnonymityData {
    checked: boolean;
    disabled: boolean;
  }

  interface RowData {
    reviewerType: string;
    anonymity: AnonymityData;
    isVisible: boolean;
  }

  interface ColumnDef {
    colId: string;
    field: string;
    displayName: string;
    minWidth: number;
    flex?: number;
    cellRenderer: (params: unknown) => JSX.Element;
  }

  const defaultColumnsDef: ColumnDef[] = useMemo(
    () => [
      {
        colId: 'Reviewer Types',
        field: 'reviewerType',
        displayName: 'Reviewer types',
        minWidth: 150,
        flex: 1,
        cellRenderer: ReviewerTypeCellRenderer,
      },
      {
        colId: 'Anonymity',
        field: 'anonymity',
        displayName: 'Reviewer identity',
        minWidth: 150,
        cellRenderer: AnonymityDropdown,
      },
    ],
    []
  );

  const defaultRowsData: RowData[] = useMemo(
    () => [
      {
        reviewerType: 'Upward',
        anonymity: { checked: true, disabled: true },
        isVisible: Boolean(hasUpward),
      },
      {
        reviewerType: 'Peer',
        anonymity: { checked: true, disabled: false },
        isVisible: Boolean(hasPeer),
      },
    ],
    [hasPeer, hasUpward]
  );

  useEffect(() => {
    const updatedColumns = defaultColumnsDef.map((column) => ({ ...column, ...commonColumnsDef }));

    setColumnDefs(updatedColumns);
  }, [commonColumnsDef, defaultColumnsDef, hasCompetencyQuestions, hasGoalQuestions, hasQNAQuestions]);

  useEffect(() => {
    const updatedRows = defaultRowsData.filter((row) => row.isVisible);

    setRowsData(updatedRows);
  }, [commonColumnsDef, defaultColumnsDef, defaultRowsData, hasCompetencyQuestions, hasGoalQuestions, hasQNAQuestions]);

  if (rowsData.length === 0) {
    return null;
  }

  return (
    <AccordionPanel2 pl="32px !important" mb="10px">
      <Flex flexDir="column" gap={3}>
        <Flex alignItems="center" gap={2}>
          <TextBodyMedium>Set the visibility of other reviewer&apos;s responses for the manager</TextBodyMedium>
          <TooltipInfoV1 title="Post the review publish, visibility of manager answers to the employees (answer means the comment and rating against a question)" />
        </Flex>
        <Box className="auto-min-height-aggrid">
          <AgGridTable
            rowData={rowsData}
            columnDefs={columnDefs}
            domLayout="autoHeight"
            containerAutoHeight
            // onGridReady={onGridReady}
            suppressScrollOnNewData
            kebabMenuIsRowSpecific={false}
            minHeight="auto"
            gridOptions={{
              rowHeight: 60,
              headerHeight: 60,
            }}
          />
        </Box>
      </Flex>
    </AccordionPanel2>
  );
};
